"use client";

import type { ClerkAPIResponseError } from "@clerk/shared/error";
import { useSignIn } from "@klarify/auth/client";
import { EmailVerificationForm } from "@klarify/ui/components/auth/email-verification-form";
import { Spinner } from "@klarify/ui/components/spinner";
import { Button } from "@klarify/ui/components/ui/button";
import { cn } from "@klarify/ui/lib/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { TwoFactorVerification } from "./two-factor-verification";

interface ForgotPasswordProps {
	initialEmail?: string;
	onBack: () => void;
	forceRedirectUrl?: string;
	redirectUrl?: string;
	onRedirectStart?: () => void;
	onTwoFactorStateChange?: (needsTwoFactor: boolean) => void;
}

export function ForgotPassword({
	initialEmail,
	onBack,
	forceRedirectUrl,
	redirectUrl,
	onRedirectStart,
	onTwoFactorStateChange,
}: ForgotPasswordProps) {
	const { t } = useLingui();
	const { isLoaded, signIn, setActive } = useSignIn();
	const [emailAddress, setEmailAddress] = useState(initialEmail ?? "");
	const [emailError, setEmailError] = useState("");
	const [generalError, setGeneralError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);

	// Step 1: Enter email and request reset
	// Step 2: Verification code
	// Step 3: Enter new password
	// Step 4: Authenticator code, only for users with 2FA turned on
	const [step, setStep] = useState<
		"request" | "verify" | "password" | "second-factor"
	>("request");
	const [newPassword, setNewPassword] = useState("");

	if (!isLoaded || isRedirecting) {
		return (
			<div className="flex min-h-100 items-center justify-center">
				<Spinner />
			</div>
		);
	}

	const handleRequestReset = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!signIn) {
			return;
		}

		setIsSubmitting(true);
		setEmailError("");
		setGeneralError("");

		try {
			await signIn.create({
				strategy: "reset_password_email_code",
				identifier: emailAddress,
			});
			setStep("verify");
			setGeneralError("");
		} catch (err: unknown) {
			const clerkError = err as ClerkAPIResponseError;
			const errorMessage =
				clerkError.longMessage ??
				clerkError?.errors?.[0]?.longMessage ??
				"An error occurred while requesting password reset.";
			const errorParam = clerkError.errors?.[0]?.meta?.paramName;

			if (errorParam === "identifier") {
				setEmailError(errorMessage);
			} else {
				setGeneralError(errorMessage);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSetPassword = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!signIn) {
			return;
		}

		setIsSubmitting(true);
		setGeneralError("");

		try {
			// At this point, the code has already been verified by EmailVerificationForm
			// Use resetPassword to set the new password
			const result = await signIn.resetPassword({
				password: newPassword,
			});

			if (result.status === "complete") {
				setIsRedirecting(true);
				onRedirectStart?.();
				await setActive({ session: result.createdSessionId });
				window.location.href = forceRedirectUrl ?? redirectUrl ?? "/";
			} else if (result.status === "needs_second_factor") {
				// Clerk asks 2FA users for their authenticator code before it signs them in.
				setStep("second-factor");
				onTwoFactorStateChange?.(true);
				setIsSubmitting(false);
			} else {
				setGeneralError(t`Password reset incomplete. Please try again.`);
				setIsSubmitting(false);
			}
		} catch (err: unknown) {
			const clerkError = err as ClerkAPIResponseError;
			const errorMessage =
				clerkError.longMessage ??
				clerkError?.errors?.[0]?.longMessage ??
				clerkError.message ??
				t`An error occurred while setting your password.`;

			// Detect expired/lost reset session
			const isExpiredSession =
				errorMessage.includes("prepare_first_factor") ||
				clerkError.code === "strategy_for_user_invalid";

			if (isExpiredSession) {
				// Send user back to Step 1 with their email pre-filled
				setNewPassword("");
				setStep("request");
				setGeneralError(
					t`Your password reset session has expired. Please request a new code to continue.`,
				);
			} else {
				setGeneralError(errorMessage);
			}
			setIsSubmitting(false);
		}
	};

	if (step === "second-factor" && signIn) {
		return (
			<TwoFactorVerification
				signIn={signIn}
				setActive={setActive}
				forceRedirectUrl={forceRedirectUrl}
				redirectUrl={redirectUrl}
				onBack={() => {
					onTwoFactorStateChange?.(false);
					onBack();
				}}
				onRedirectStart={onRedirectStart}
			/>
		);
	}

	// Step 1: Request password reset
	if (step === "request") {
		return (
			<div className="w-full max-w-md rounded-xl border bg-white p-7 shadow-custom lg:p-8">
				<div className="mb-6">
					<h1 className="font-semibold text-2xl text-klarify-gray-mod-900">
						<Trans>Forgot your password?</Trans>
					</h1>
					<p className="mt-2 text-klarify-gray-mod-600 text-sm">
						<Trans>
							Enter your email address and we'll send you a code to reset your
							password.
						</Trans>
					</p>
				</div>

				<form
					onSubmit={handleRequestReset}
					className="space-y-4"
				>
					<div>
						<label
							htmlFor="email"
							className="mb-1.5 block font-medium text-klarify-gray-mod-700 text-sm"
						>
							<Trans>Email address</Trans>
						</label>
						<input
							type="email"
							id="email"
							value={emailAddress}
							onChange={(e) => setEmailAddress(e.target.value)}
							className={cn(
								"w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1",
								emailError
									? "border-destructive focus:border-destructive focus:ring-destructive"
									: "border-klarify-gray-mod-300 focus:border-primary focus:ring-primary",
							)}
							required
						/>
						{emailError && (
							<p className="mt-1.5 text-destructive text-sm">{emailError}</p>
						)}
					</div>

					{generalError && (
						<div className="rounded-md bg-destructive/5 p-3 text-destructive text-sm">
							{generalError}
						</div>
					)}

					<Button
						type="submit"
						disabled={isSubmitting}
						rounding="md"
						className="mt-2 w-full"
					>
						{isSubmitting ? (
							<Trans>Sending code...</Trans>
						) : (
							<Trans>Send reset code</Trans>
						)}
					</Button>

					<div className="mt-2 text-center">
						<Button
							type="button"
							variant="link"
							onClick={onBack}
							className="h-auto p-0 text-klarify-gray-mod-600 text-sm hover:text-klarify-gray-mod-900"
						>
							<Trans>Back to sign in</Trans>
						</Button>
					</div>
				</form>
			</div>
		);
	}

	// Step 2: Verify code
	if (step === "verify") {
		return (
			<div className="w-full max-w-md rounded-xl border bg-white p-7 shadow-custom lg:p-8">
				<div className="mb-6">
					<h1 className="font-semibold text-2xl text-klarify-gray-mod-900">
						<Trans>Reset Password</Trans>
					</h1>
				</div>

				<EmailVerificationForm
					mode="reset-password"
					onVerified={() => setStep("password")}
					onRedirectStart={onRedirectStart}
				/>

				<div className="mt-4 text-center">
					<Button
						type="button"
						variant="link"
						onClick={() => {
							setStep("request");
							setGeneralError("");
						}}
						className="h-auto p-0 text-klarify-gray-mod-600 text-sm hover:text-klarify-gray-mod-900"
					>
						<Trans>Try a different email</Trans>
					</Button>
				</div>
			</div>
		);
	}

	// Step 3: Set new password
	return (
		<div className="w-full max-w-md rounded-xl border bg-white p-7 shadow-custom lg:p-8">
			<div className="mb-6">
				<h1 className="font-semibold text-2xl text-klarify-gray-mod-900">
					<Trans>Set new password</Trans>
				</h1>
				<p className="mt-2 text-klarify-gray-mod-600 text-sm">
					<Trans>Enter a new password for your account</Trans>
				</p>
			</div>

			<form
				onSubmit={handleSetPassword}
				className="space-y-4"
			>
				<div>
					<label
						htmlFor="newPassword"
						className="mb-1.5 block font-medium text-klarify-gray-mod-700 text-sm"
					>
						<Trans>New password</Trans>
					</label>
					<input
						type="password"
						id="newPassword"
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						className="w-full rounded-md border border-klarify-gray-mod-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
						required
					/>
				</div>

				{generalError && (
					<div className="rounded-md bg-destructive/5 p-3 text-destructive text-sm">
						{generalError}
					</div>
				)}

				<Button
					type="submit"
					disabled={isSubmitting}
					rounding="md"
					className="mt-2 w-full"
				>
					{isSubmitting ? (
						<Trans>Setting password...</Trans>
					) : (
						<Trans>Set password</Trans>
					)}
				</Button>
			</form>
		</div>
	);
}
